import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import './LayoutComponents.css';
import { FaGlobe, FaSignOutAlt, FaUser, FaChevronDown, FaCog, FaBars } from 'react-icons/fa';
import timesheetLogo from '../../assets/images/timesheet-logo.png';
import UserAvatar from '../UserAvatar';
import { useTranslation } from 'react-i18next';
import { setLanguage, setOptions } from '../../store/slices/languageSlice';
import { useSidebar } from './SidebarContext';
import { useAuthToken } from '../../auth';

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logout } = useAuthToken();
  const { user } = useSelector((state) => state.auth);
  const { toggle: toggleSidebar } = useSidebar();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { t, i18n } = useTranslation();
  const languageState = useSelector((state) => state.language || {}); 
  const currentLang = languageState.selected || i18n.language || 'en';
  const langOptions = languageState.options || []; 

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // ensure redux and i18n stay in sync (layout: before paint, avoids EN flash on login header)
  useLayoutEffect(() => {
    if (currentLang && i18n.language !== currentLang) {
      void i18n.changeLanguage(currentLang);
    }
  }, [currentLang, i18n]);

  // initialize language options in redux if not present (persisted across logins)
  useEffect(() => {
    if (!Array.isArray(langOptions) || langOptions.length === 0) {
      const defaultCodes = ['en', 'zh'];
      const opts = defaultCodes.map((code) => ({
        code,
        label: i18n.t(`languages.${code}`) || (code === 'en' ? 'English' : '中文'),
      }));
      dispatch(setOptions(opts));
    }
    // update labels when language changes (keeps labels localized)
  }, [dispatch, langOptions, i18n.language, i18n]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (code) => {
    if (!code) return;
    dispatch(setLanguage(code));
    i18n.changeLanguage(code);
  };

  return (
    <nav className="navbar-container login-navbar-container app-navbar">
      <div className="navbar-left">
        <button
          type="button"
          className="app-navbar-menu-button"
          onClick={toggleSidebar}
          aria-label={t('navbar.openMenu')}
        >
          <FaBars aria-hidden="true" />
        </button>
      </div>
      <div className="navbar-right">
        <div className="navbar-profile-dropdown" ref={dropdownRef}>
          <button className="navbar-profile-button" onClick={toggleDropdown}>
            <UserAvatar avatar={user?.avatar} className="navbar-profile-picture" size={40} />
            <FaChevronDown className={`dropdown-arrow ${isDropdownOpen ? 'rotated' : ''}`} />
          </button>
          {isDropdownOpen && (
            <div className="navbar-dropdown-menu">
              <div className="dropdown-profile-info">
                <UserAvatar avatar={user?.avatar} className="dropdown-profile-picture" size={48} />
                <div className="dropdown-user-info">
                  <div className="dropdown-username">{user?.userName || user?.username || 'User'}</div>
                  <div className="dropdown-jobtitle">{user?.title?.titleName || user?.role || ''}</div>
                </div>
              </div>
              <div className="dropdown-divider"></div>
              <Link to="/profile" className="dropdown-link" onClick={() => setIsDropdownOpen(false)}>
                <FaUser />
                {t('navbar.profile')}
              </Link>
              <Link to="/settings" className="dropdown-link" onClick={() => setIsDropdownOpen(false)}>
                <FaCog />
                {t('navbar.settings')}
              </Link>
              <button className="dropdown-link logout-link" onClick={handleLogout}>
                <FaSignOutAlt />
                {t('navbar.logout')}
              </button>
            </div>
          )}
        </div>
        <div className="navbar-language-wrapper">
          <FaGlobe />
          <select
            className="navbar-language-select"
            value={currentLang}
            onChange={(e) => handleLanguageChange(e.target.value)}
          >
            {langOptions.length > 0 ? langOptions.map((o) => (
              <option key={o.code} value={o.code}>
                {o.label}
              </option>
            )) : (
              <>
                <option value="en">English</option>
                <option value="zh">中文</option>
              </>
            )}
          </select>
        </div>
      </div>
    </nav>
  );
};

// LoginNavbar
export const LoginNavbar = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const tn = (key) => t(`loginNavbar.${key}`);
  const languageState = useSelector((state) => state.language || {}); 
  const currentLang = languageState.selected || i18n.language || 'en';
  const langOptions = languageState.options || [];
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const navLinks = [
    { to: '/home', label: tn('home') },
    { to: '/about', label: tn('about') },
    { to: '/contact', label: tn('contact') },
  ];

  // ensure redux and i18n stay in sync (layout: before paint)
  useLayoutEffect(() => {
    if (currentLang && i18n.language !== currentLang) {
      void i18n.changeLanguage(currentLang);
    }
  }, [currentLang, i18n]);

  // initialize language options in redux if not present (persisted across logins)
  useEffect(() => {
    if (!Array.isArray(langOptions) || langOptions.length === 0) {
      const defaultCodes = ['en', 'zh'];
      const opts = defaultCodes.map((code) => ({
        code,
        label: i18n.t(`languages.${code}`) || (code === 'en' ? 'English' : '中文'),
      }));
      dispatch(setOptions(opts));
    }
    // update labels when language changes (keeps labels localized)
  }, [dispatch, langOptions, i18n.language, i18n]);

  const handleLanguageChange = (code) => {
    if (!code) return;
    dispatch(setLanguage(code));
    i18n.changeLanguage(code);
  };

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const scrollY = window.scrollY;
    document.body.classList.add('login-navbar-open');
    document.documentElement.classList.add('login-navbar-open');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflowX = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('login-navbar-open');
      document.documentElement.classList.remove('login-navbar-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflowX = '';
      window.scrollTo(0, scrollY);
    };
  }, [menuOpen]);

  return (
    <div className="login-navbar-wrapper">
      <nav className="navbar-container login-navbar-container">
        <button
          type="button"
          className="login-navbar-menu-button"
          onClick={toggleMenu}
          aria-label={menuOpen ? t('navbar.closeMenu') : t('navbar.openMenu')}
          aria-expanded={menuOpen}
        >
          <FaBars aria-hidden="true" />
        </button>
        <div className="navbar-left">
          <img src={timesheetLogo} alt="EZ Timesheet Logo" className="navbar-logo-image" />
          <span className="navbar-app-name">EZ Timesheet</span>
        </div>
        <div className="navbar-center login-navbar-desktop-links">
          {navLinks.map((link) => (
            <Link key={link.to} to={link.to} className="navbar-link">
              {link.label}
            </Link>
          ))}
        </div>
        <div className="navbar-right">
          <div className="navbar-language-wrapper">
            <FaGlobe />
            <select
              className="navbar-language-select"
              value={currentLang}
              onChange={(e) => handleLanguageChange(e.target.value)}
            >
              {langOptions.length > 0 ? langOptions.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              )) : (
                <>
                  <option value="en">English</option>
                  <option value="zh">中文</option>
                </>
              )}
            </select>
          </div>
        </div>
      </nav>
      {menuOpen && (
        <>
          <button
            type="button"
            className="login-navbar-backdrop"
            aria-label={t('navbar.closeMenu')}
            onClick={closeMenu}
          />
          <div className="login-navbar-dropdown" role="menu">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="login-navbar-dropdown-link"
                role="menuitem"
                onClick={closeMenu}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Navbar; 